import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErrorState } from "@/components/shared/ErrorState";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { physicalClientsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/formatters";
import { TablePagination } from "../../CustomerGroups/component/TablePagination";
import { filterAccessLog } from "./physicalClientTableSearch";
import { TableSearchField } from "./TableSearchField";
import { useLocalTable } from "./useLocalTable";

interface DocumentAccessLogTableProps {
  physicalClientId: number;
  queryKey: readonly unknown[];
}

/**
 * Quem viu o CPF completo, quando e por quê (sem o CPF), mais recente primeiro, com busca e paginação na tela: a API
 * devolve todos os acessos do cliente.
 */
export function DocumentAccessLogTable({ physicalClientId, queryKey }: DocumentAccessLogTableProps) {
  const query = useQuery({
    queryKey,
    queryFn: async () => (await physicalClientsApi.getDocumentAccessLog(physicalClientId)).data ?? [],
  });
  const accesses = useMemo(() => query.data ?? [], [query.data]);
  const table = useLocalTable(accesses, filterAccessLog);

  if (query.isLoading) return <Skeleton className="h-32 w-full" aria-busy="true" />;
  if (query.isError)
    return <ErrorState message="Não foi possível carregar os acessos ao CPF." onRetry={() => void query.refetch()} />;
  if (!accesses.length)
    return <EmptyState title="Nenhum acesso registrado" message="Ninguém viu o CPF completo deste cliente." className="py-6" />;

  return (
    <div className="space-y-4">
      <TableSearchField
        label="Buscar nos acessos"
        placeholder="Quem, motivo ou data"
        value={table.search}
        onChange={table.setSearch}
      />

      {table.filtered.length === 0 ? (
        <EmptyState title="Nenhum acesso encontrado" message="Revise o texto da busca." className="py-6" />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quem</TableHead>
                  <TableHead>Quando</TableHead>
                  <TableHead>Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.pageItems.map((access) => (
                  <TableRow key={access.id}>
                    <TableCell>{access.accessedBy}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(new Date(access.accessedAt))}</TableCell>
                    <TableCell>{access.reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            page={table.page}
            pageSize={table.pageSize}
            total={table.filtered.length}
            unfilteredTotal={accesses.length}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        </>
      )}
    </div>
  );
}
